import {
  DEFAULT_INITIAL_STATE,
  InitialStateProps,
  ModeWithSceneFilterInitialState,
} from "@/modes/mode";
import {
  useComputeValidModeElement,
  useCurrentArea,
} from "@/modes/mode-data-context";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { useAppStore } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import {
  GUID,
  includes,
  PropOptional,
  validatePrimitive,
} from "@faro-lotv/foundation";
import { IElement } from "@faro-lotv/ielement-types";
import { uniqBy } from "es-toolkit";
import { useCallback, useMemo } from "react";
import { selectIsElementViewable } from "../mode-selectors";
import {
  selectIsWalkSceneActiveElement,
  WalkSceneActiveElement,
} from "./walk-types";

/**
 * Analyze the current scene and adjust it so it can work for walk mode
 * Makes sure the active element is one of the valid sessions for a walk scene
 *
 * @param fallback The function called if no valid model was found
 * @returns the main object walk mode can render
 */
export function useComputeValidWalkScene(
  fallback: (() => void) | undefined = undefined,
): WalkSceneActiveElement | undefined {
  const { dataSessions2d, dataSessions3d, dataSessions5d, roomsSections } =
    useCurrentArea();

  const { getState } = useAppStore();

  const validSessions = useMemo(
    () =>
      // A session can be listed both as a splat session and as a 2d/3d/5d one
      uniqBy(
        [
          ...dataSessions2d,
          ...dataSessions3d.pointClouds,
          ...dataSessions3d.gaussianSplats,
          ...dataSessions5d,
          ...roomsSections,
        ],
        (session) => session.id,
      ),
    [dataSessions2d, dataSessions3d, dataSessions5d, roomsSections],
  );

  const validModelGuard = useCallback(
    (el: IElement): el is WalkSceneActiveElement =>
      selectIsWalkSceneActiveElement(el)(getState()) &&
      selectIsElementViewable(el)(getState()),
    [getState],
  );

  return useComputeValidModeElement(validSessions, validModelGuard, fallback);
}

/** An object to compute and parse a state object to create and restore the walk mode states from deep links */
export const WALK_MODE_INITIAL_STATE: InitialStateProps<ModeWithSceneFilterInitialState> =
  {
    compute(state, viewContext) {
      return {
        ...DEFAULT_INITIAL_STATE.compute(state, viewContext),
        scene: selectFlyWalkSceneFilter(state),
      };
    },
    parse(appState, modeState) {
      let lookAtId: GUID | undefined;
      const scene = includes(Object.values(SceneFilter), modeState.scene)
        ? modeState.scene
        : undefined;
      // lookAtId is not defined when coming back from the Draft view, so no error should be
      // logged for this standard case.
      if (validatePrimitive(modeState, "lookAtId", "string", PropOptional)) {
        ({ lookAtId } = modeState);
      }
      return {
        ...DEFAULT_INITIAL_STATE.parse(appState, modeState),
        scene,
        lookAtId,
      };
    },
  };
